import { useEffect, useRef, useState } from "react";
import { useIsWriteRestrictedUser } from "../../hooks/useIsWriteRestrictedUser";
import { useDeadbandSetpoints } from "../../hooks/useDeadbandSetpoints";
import {
  SCHEDULE_MODE_OPTIONS,
  SCHEDULE_FAN_STEP_ORDER,
  DEFAULT_CONTROL_VALUES,
  HEAT_COLOR,
  COOL_COLOR,
  FAN_SPEED_COLOR,
} from "../../utils/constants";
import {
  SCHEDULE_DAYS_ORDER,
  SCHEDULE_DAY_LABELS,
  minuteOfDayToTimeInputValue,
  timeInputValueToMinuteOfDay,
  findConflictingEventIndex,
} from "../../utils/scheduleUtils";
import { clampValue, resolveDisplaySetpoints } from "../../utils/setpointUtils";
import { convertCelsiusToFahrenheit, convertFahrenheitToCelsius } from "../../utils/common_utils";
import ColorDropdown from "../Common/ColorDropdown";
import HorizontalStepperSlider from "../Common/HorizontalStepperSlider";
import ConfirmModal from "../Common/ConfirmModal";
import Button from "../Common/Button";
import Label from "../Common/Label";

const DEFAULT_HEAT_TARGET_TEMP_F = 68;
const DEFAULT_COOL_TARGET_TEMP_F = 75;

const buildInitialFormState = (event, heatMaxF, coolMinF) => ({
  timeInputValue: minuteOfDayToTimeInputValue(event?.minuteOfDay ?? 480),
  selectedDays: event?.daysOfWeek ?? [],
  mode: event?.eventType ?? "HEAT_AND_COOL",
  fanSetting: event?.fanSetting ?? "AUTO",
  heatTargetTempF:
    event?.heatTargetTempC != null
      ? convertCelsiusToFahrenheit(event.heatTargetTempC)
      : clampValue(
          DEFAULT_HEAT_TARGET_TEMP_F,
          DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F,
          heatMaxF
        ),
  coolTargetTempF:
    event?.coolTargetTempC != null
      ? convertCelsiusToFahrenheit(event.coolTargetTempC)
      : clampValue(
          DEFAULT_COOL_TARGET_TEMP_F,
          coolMinF,
          DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F
        ),
});

const ScheduleEventModal = ({
  event,
  eventIndex,
  existingEvents,
  fallbackHeatingSetPoint,
  fallbackCoolingSetPoint,
  isLastRemainingEvent,
  isScheduleEnabled,
  isSaving,
  onCancel,
  onSave,
  onDelete,
}) => {
  const isAccessRestricted = useIsWriteRestrictedUser();
  const timeInputRef = useRef(null);

  const resolvedHeatMax = fallbackHeatingSetPoint ?? DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F;
  const resolvedCoolMin = fallbackCoolingSetPoint ?? DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F;

  const [formState, setFormState] = useState(() =>
    buildInitialFormState(event, resolvedHeatMax, resolvedCoolMin)
  );
  const [isDisplayingDeleteConfirmModal, setIsDisplayingDeleteConfirmModal] = useState(false);
  const [pendingSaveEventValues, setPendingSaveEventValues] = useState(null);
  const [conflictError, setConflictError] = useState(null);

  const { safeSetHeatTargetTempF, safeSetCoolTargetTempF, resetLastAdjustedRangeBar } = useDeadbandSetpoints({
    mode: formState.mode,
    heatTargetTempF: formState.heatTargetTempF,
    coolTargetTempF: formState.coolTargetTempF,
    setHeatTargetTempF: (value) => setFormState((prev) => ({ ...prev, heatTargetTempF: value })),
    setCoolTargetTempF: (value) => setFormState((prev) => ({ ...prev, coolTargetTempF: value })),
    heatMaxF: resolvedHeatMax,
    coolMinF: resolvedCoolMin,
  });

  useEffect(() => {
    setFormState(buildInitialFormState(event, resolvedHeatMax, resolvedCoolMin));
    resetLastAdjustedRangeBar();
  }, [event, resolvedHeatMax, resolvedCoolMin, resetLastAdjustedRangeBar]);

  const needsHeatSetpoint = formState.mode === "HEAT" || formState.mode === "HEAT_AND_COOL";
  const needsCoolSetpoint =
    formState.mode === "COOL" || formState.mode === "DRY" || formState.mode === "HEAT_AND_COOL";
  const showSetpoint = needsHeatSetpoint || needsCoolSetpoint;
  const showFan = formState.mode !== "OFF" && formState.mode !== "DRY";
  const minuteOfDay = timeInputValueToMinuteOfDay(formState.timeInputValue);
  const isSaveDisabled =
    isAccessRestricted ||
    formState.selectedDays.length === 0 ||
    !Number.isFinite(minuteOfDay) ||
    isSaving;

  const { heatF: displayHeatSetpointF, coolF: displayCoolSetpointF } = resolveDisplaySetpoints(
    formState.mode,
    formState.heatTargetTempF,
    formState.coolTargetTempF,
    resolvedCoolMin,
    resolvedHeatMax
  );

  const toggleDay = (day) => {
    setConflictError(null);
    setFormState((prev) => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(day)
        ? prev.selectedDays.filter((selectedDay) => selectedDay !== day)
        : [...prev.selectedDays, day],
    }));
  };

  const handleTimeChange = (e) => {
    setConflictError(null);
    setFormState((prev) => ({ ...prev, timeInputValue: e.target.value }));
  };

  const handleSave = () => {
    if (isSaveDisabled) return;

    const conflictIndex = findConflictingEventIndex(
      existingEvents ?? [],
      formState.selectedDays,
      minuteOfDay,
      eventIndex ?? -1
    );
    if (conflictIndex !== -1) {
      setConflictError("Another event is already scheduled on one of these days at this same time.");
      return;
    }

    const eventValues = {
      daysOfWeek: formState.selectedDays,
      minuteOfDay,
      eventType: formState.mode,
      fanSetting: formState.mode === "DRY" ? "AUTO" : showFan ? formState.fanSetting : undefined,
      heatTargetTempC: needsHeatSetpoint
        ? convertFahrenheitToCelsius(formState.heatTargetTempF)
        : undefined,
      coolTargetTempC: needsCoolSetpoint
        ? convertFahrenheitToCelsius(formState.coolTargetTempF)
        : undefined,
    };

    if (!isScheduleEnabled) {
      setPendingSaveEventValues(eventValues);
      return;
    }
    onSave(eventValues);
  };

  const fanStepIndex = Math.max(SCHEDULE_FAN_STEP_ORDER.indexOf(formState.fanSetting), 0);

  return (
    <div
      className="overlayHolder bg-black/30 dark:bg-black/60 fixed inset-0 flex justify-center top-0 z-[999]"
      data-testid="schedule-event-modal"
    >
      <div className="modalHlder bg-ivory dark:bg-darkThemeModalBg rounded-2xl border border-black/10 w-full md:w-[560px] max-h-[90%] my-auto p-6 lg:p-8 overflow-auto mx-5">
        <div className="flex items-center justify-between gap-4">
          <Button
            onClick={onCancel}
            disabled={isSaving}
            data-testid="schedule-event-cancel"
            aria-label="Close"
            className={`size-8 shrink-0 flex items-center justify-center text-dark-grey dark:text-white ${
              isSaving ? "opacity-40 cursor-not-allowed" : ""
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
          <h2 className="modalTitle text-dark-grey dark:text-white text-base font-bold tracking-[0.03px] uppercase">
            {event ? "Edit Schedule" : "Create Schedule"}
          </h2>
          <Button
            onClick={handleSave}
            disabled={isSaveDisabled}
            data-testid="schedule-event-save"
            className={`shrink-0 text-orange-500 text-base font-bold tracking-[0.03px] uppercase ${
              isSaveDisabled ? "opacity-40 cursor-not-allowed" : ""
            }`}
          >
            Save
          </Button>
        </div>

        <div className="mt-8 flex flex-col items-center gap-1">
          <Label
            htmlFor="scheduleEventTime"
            className="text-dark-grey dark:text-white text-xs font-semibold tracking-[0.03px] uppercase"
          >
            Schedule Time
          </Label>
          <input
            id="scheduleEventTime"
            ref={timeInputRef}
            type="time"
            data-testid="schedule-event-time-input"
            className="text-3xl font-bold text-center text-dark-grey dark:text-white bg-transparent border-none focus:outline-none dark:[&::-webkit-calendar-picker-indicator]:invert"
            value={formState.timeInputValue}
            onChange={handleTimeChange}
            onClick={() => timeInputRef.current?.showPicker?.()}
          />
        </div>

        <div className="mt-8 flex flex-col items-center gap-3">
          <span className="text-dark-grey dark:text-white text-xs font-semibold tracking-[0.03px] uppercase">
            Repeat
          </span>
          <div className="flex flex-wrap justify-center gap-2">
            {SCHEDULE_DAYS_ORDER.map((day) => {
              const isSelected = formState.selectedDays.includes(day);
              return (
                <Button
                  key={day}
                  aria-pressed={isSelected}
                  data-testid={`schedule-event-day-${day.toLowerCase()}`}
                  onClick={() => toggleDay(day)}
                  className={`size-9 rounded-full border-2 text-sm font-semibold transition-colors duration-200 ${
                    isSelected
                      ? "bg-dark-grey border-dark-grey text-white dark:bg-white dark:border-white dark:text-dark-grey"
                      : "bg-white dark:bg-transparent border-grey text-dark-grey dark:text-white"
                  }`}
                >
                  {SCHEDULE_DAY_LABELS[day]}
                </Button>
              );
            })}
          </div>
          {conflictError && (
            <p
              className="text-critical text-xs text-center mt-1"
              data-testid="schedule-event-conflict-error"
            >
              {conflictError}
            </p>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-2">
          <span className="text-dark-grey dark:text-white text-xs font-semibold tracking-[0.03px] uppercase">
            Mode
          </span>
          <ColorDropdown
            options={SCHEDULE_MODE_OPTIONS}
            selectedValue={formState.mode}
            onSelect={(mode) => setFormState((prev) => ({ ...prev, mode }))}
            placeholder="Set Mode"
            data-testid="schedule-event-mode-dropdown"
          />
        </div>

        {showSetpoint && (
          <div className="mt-8 flex flex-col gap-3">
            {needsHeatSetpoint && (
              <HorizontalStepperSlider
                min={DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F}
                max={resolvedHeatMax}
                value={displayHeatSetpointF}
                onChange={safeSetHeatTargetTempF}
                formatValue={(v) => `${v}°`}
                color={HEAT_COLOR}
                ariaLabel="Heat Setpoint"
                data-testid="schedule-event-heat-slider"
              />
            )}
            {needsCoolSetpoint && (
              <HorizontalStepperSlider
                min={resolvedCoolMin}
                max={DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F}
                value={displayCoolSetpointF}
                onChange={safeSetCoolTargetTempF}
                formatValue={(v) => `${v}°`}
                color={COOL_COLOR}
                ariaLabel="Cool Setpoint"
                data-testid="schedule-event-cool-slider"
              />
            )}
          </div>
        )}

        {showFan && (
          <div className="mt-8">
            <HorizontalStepperSlider
              min={0}
              max={SCHEDULE_FAN_STEP_ORDER.length - 1}
              value={fanStepIndex}
              onChange={(index) =>
                setFormState((prev) => ({ ...prev, fanSetting: SCHEDULE_FAN_STEP_ORDER[index] }))
              }
              formatValue={(index) => SCHEDULE_FAN_STEP_ORDER[index]}
              color={FAN_SPEED_COLOR}
              label="Fan Speed"
              data-testid="schedule-event-fan-stepper"
            />
          </div>
        )}

        {onDelete && (
          <Button
            onClick={() => setIsDisplayingDeleteConfirmModal(true)}
            disabled={isAccessRestricted || isSaving}
            data-testid="schedule-event-delete"
            className={`mt-8 w-full bg-critical text-white px-5 py-3 rounded-md font-semibold leading-5 hover:bg-critical/90 ${
              isAccessRestricted || isSaving ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            Delete Schedule
          </Button>
        )}
      </div>

      {isDisplayingDeleteConfirmModal && (
        <ConfirmModal
          title="Delete Schedule Event"
          subText=""
          confirmMessage={
            isLastRemainingEvent ? (
              <p className="my-4">
                This is the last event on this schedule. Deleting it will delete the entire schedule
                and turn it off for every unit in this zone.
              </p>
            ) : !isScheduleEnabled ? (
              <p className="my-4">
                The schedule is currently off. Deleting this event will turn it back on for every
                unit in this zone.
              </p>
            ) : (
              <p className="my-4">This schedule event will be removed for every unit in this zone.</p>
            )
          }
          cancelText="Cancel"
          submitText="Delete"
          onCancel={() => setIsDisplayingDeleteConfirmModal(false)}
          onSubmit={() => {
            setIsDisplayingDeleteConfirmModal(false);
            onDelete();
          }}
        />
      )}

      {pendingSaveEventValues && (
        <ConfirmModal
          title="Turn Schedule Back On?"
          subText=""
          confirmMessage={
            <p className="my-4">
              The schedule is currently off. Saving this event will turn it back on for every unit
              in this zone.
            </p>
          }
          cancelText="Cancel"
          submitText="Save & Turn On"
          onCancel={() => setPendingSaveEventValues(null)}
          onSubmit={() => {
            const eventValues = pendingSaveEventValues;
            setPendingSaveEventValues(null);
            onSave(eventValues);
          }}
        />
      )}
    </div>
  );
};

export default ScheduleEventModal;
